/* pi-unit-variants (#238): when a multi-unit tier is selected on a product with
   real variants, render one dropdown PER UNIT so unit 1 and unit 2 can be
   DIFFERENT variants. Talks to each PI section via its root element's __geUV
   bridge (set by the main PI script). Liquid-free ON PURPOSE - the deploy
   pipeline externalizes this block to a shared asset (lib/pi-section-split.js),
   so it adds ~0 bytes to the 256KB section budget. Dropdowns ALWAYS (native
   selects stay clean with many variants, and on mobile). */
(function () {
  'use strict';

  /* Pure core - exposed for tests (harmless in production).
     Pricing: the tier's baked data-price-cents encodes the DISCOUNTED total for
     qty x the render-time basis variant (data-basis-cents). The same discount
     RATIO is applied to whatever mix of units the buyer picks:
       total = round(sum(unit prices) * tierCents / (basisCents * qty))
     When every unit is the basis variant the sum telescopes and total ==
     tierCents exactly - no drift against the baked tier price. */
  function uvComputeTotalCents(unitIds, priceById, tierCents, basisCents, qty) {
    var sum = 0;
    for (var i = 0; i < unitIds.length; i++) sum += priceById[String(unitIds[i])] || 0;
    var ratio = (tierCents > 0 && basisCents > 0 && qty > 0) ? tierCents / (basisCents * qty) : 1;
    return Math.round(sum * ratio);
  }
  function uvMergeLines(unitIds) {
    var counts = {}, order = [];
    for (var i = 0; i < unitIds.length; i++) {
      var id = String(unitIds[i]);
      if (!counts[id]) { counts[id] = 0; order.push(id); }
      counts[id] += 1;
    }
    return order.map(function (id) { return { id: id, quantity: counts[id] }; });
  }
  /* Picker-style resolution (#238b + uv_style): an explicit merchant choice
     (the block's "Variant picker style" select, stamped as data-uv-style on
     the offer container) wins; anything else — 'auto', absent, junk — falls
     back to the original heuristic: simple products (<=6 variants, single
     option dimension) get pills, everything else the dropdown. */
  function uvPickMode(styleAttr, variantCount, optCount) {
    if (styleAttr === 'pills' || styleAttr === 'swatches' || styleAttr === 'dropdown') return styleAttr;
    return (variantCount <= 6 && optCount <= 1) ? 'pills' : 'dropdown';
  }
  /* Option axes (#uv-option-split): a 2-3 option product (Size x Color) must
     offer one picker PER OPTION, never one combined "S / Blue" matrix list.
     Axes derive from the product's option names + the available variants'
     option1..3 values (first-appearance order); a single-value axis is noise
     and is dropped. Option names tolerate both product|json shapes (plain
     strings and {name} objects). Pure + exposed for the spec. */
  function uvAxes(optionNames, variants) {
    var names = Array.isArray(optionNames) ? optionNames : [];
    var axes = [];
    for (var k = 0; k < 3 && k < names.length; k++) {
      var seen = {}, values = [];
      for (var i = 0; i < variants.length; i++) {
        var val = variants[i] && variants[i]['option' + (k + 1)];
        if (typeof val !== 'string' || !val) continue;
        if (!seen[val]) { seen[val] = 1; values.push(val); }
      }
      if (values.length > 1) {
        var nm = names[k];
        axes.push({ index: k, name: typeof nm === 'string' ? nm : ((nm && nm.name) || ''), values: values });
      }
    }
    return axes;
  }
  /* Resolve the variant for changing ONE axis while keeping the others:
     exact combination first; else the first variant sharing the new value
     (nearest match — a missing combination snaps a sibling axis instead of
     wedging); else keep the current variant. Pure + exposed for the spec. */
  function uvResolveVariant(variants, currentId, axisIndex, value) {
    var cur = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(currentId)) { cur = variants[i]; break; }
    }
    var partial = null;
    for (var j = 0; j < variants.length; j++) {
      var v = variants[j];
      if (v['option' + (axisIndex + 1)] !== value) continue;
      var exact = true;
      for (var k = 0; k < 3; k++) {
        if (k === axisIndex) continue;
        var want = cur ? cur['option' + (k + 1)] : null;
        if (want != null && v['option' + (k + 1)] !== want) { exact = false; break; }
      }
      if (exact) return v;
      if (!partial) partial = v;
    }
    return partial || cur || variants[0];
  }
  try { window.__geUVCore = { computeTotalCents: uvComputeTotalCents, mergeLines: uvMergeLines, pickMode: uvPickMode, axes: uvAxes, resolveVariant: uvResolveVariant }; } catch (e) {}

  function initUnitVars(root) {
    var uv = root.__geUV;
    if (!uv || !uv.data) return;
    var all = Array.isArray(uv.data.variants) ? uv.data.variants : [];
    var variants = all.filter(function (v) { return v && v.available !== false; });
    if (variants.length < 2) return; /* single variant (incl. Default Title) - nothing to pick */
    var priceById = {}, titleById = {};
    for (var i = 0; i < variants.length; i++) {
      priceById[String(variants[i].id)] = variants[i].price || 0;
      titleById[String(variants[i].id)] = variants[i].title || String(variants[i].id);
    }

    var panel = document.createElement('div');
    panel.className = 'pi-uv-panel';
    panel.setAttribute('data-ge-uv-panel', '');
    panel.hidden = true;
    /* After the tier list; fall back to just above the ATC button. */
    var tiersWrap = root.querySelector('[data-tier-count]');
    if (tiersWrap && tiersWrap.parentNode) tiersWrap.parentNode.insertBefore(panel, tiersWrap.nextSibling);
    else {
      var atc = root.querySelector('[data-pi-atc]');
      if (atc && atc.parentNode) atc.parentNode.insertBefore(panel, atc);
      else return;
    }

    var unitIds = [];

    function tierInfo() {
      var t = uv.getTier ? uv.getTier() : null;
      var qty = (t && parseInt(t.dataset.qty, 10)) || (uv.getQty ? uv.getQty() : 1) || 1;
      return {
        qty: qty,
        cents: (t && parseInt(t.dataset.priceCents, 10)) || 0,
        basis: (t && parseInt(t.dataset.basisCents, 10)) || 0
      };
    }

    function seedVariantId() {
      var cur = uv.getVariant ? uv.getVariant() : null;
      if (cur && priceById[String(cur.id)] !== undefined) return String(cur.id);
      return String(variants[0].id);
    }

    function recompute() {
      var info = tierInfo();
      if (info.qty < 1) { uv.items = null; return; }
      uv.items = uvMergeLines(unitIds);
      var total = uvComputeTotalCents(unitIds, priceById, info.cents, info.basis, info.qty);
      if (total > 0 && uv.setPrice) uv.setPrice(total);
      /* #tier-price-sync: the SELECTED tier card must show the SAME number as
         the ATC button — a premium variant pick used to move the button while
         the card sat at the baked basis price (static). Only the main price
         node syncs; compare/per-unit strings stay baked (the tier's discount
         RATIO is constant, so the Save % badge stays truthful), and an
         all-basis pick telescopes back to the exact baked price. */
      if (total > 0 && typeof uv.money === 'function') {
        var tEl = uv.getTier ? uv.getTier() : null;
        var disp = tEl && tEl.querySelector('[data-tier-price-display]');
        if (disp) disp.textContent = uv.money(total);
      }
    }

    function render() {
      var info = tierInfo();
      /* #tier-vs-dedup follow-up: the panel renders from qty >= 1 (not >= 2).
         With the standalone variant_selector hidden whenever a tiers-mode
         tier_selector exists, this panel IS the page's only variant picker —
         a 1-unit tier (or preselect '1') must still offer the choice. */
      if (info.qty < 1) { panel.hidden = true; panel.innerHTML = ''; unitIds = []; uv.items = null; return; }
      var seed = seedVariantId();
      while (unitIds.length < info.qty) unitIds.push(seed);
      unitIds.length = info.qty;
      /* Design pick — uvPickMode (pure, exposed on __geUVCore for tests):
         explicit choice from the block setting wins, 'auto' keeps the #238b
         heuristic. Swatches render as pills whose body is ONLY the colour
         circle (initials circle when the name isn't a CSS colour). */
      var styleHost = root.querySelector('[data-uv-style]');
      var uvStyle = (styleHost && styleHost.getAttribute('data-uv-style')) || 'auto';
      var optCount = Array.isArray(uv.data.options) ? uv.data.options.length : 1;
      var uvMode = uvPickMode(uvStyle, variants.length, optCount);
      var usePills = uvMode !== 'dropdown';
      var swatchOnly = uvMode === 'swatches';
      /* #uv-option-split: 2+ real option axes (Size x Color) render one
         picker PER AXIS — the combined "S / Blue" matrix list is exactly the
         merchant complaint. Overrides uv_style (pills on a matrix explode
         combinatorially). The image chip rides the colour-ish axis and shows
         the resolved variant's own photo. */
      var axes = uvAxes(uv.data.options, variants);
      var multiAxis = axes.length >= 2;
      var imgAxisIndex = -1;
      if (multiAxis) {
        for (var ax = 0; ax < axes.length; ax++) {
          if (/colou?r|shade|pattern|style|design|material|finish/i.test(String(axes[ax].name))) { imgAxisIndex = axes[ax].index; break; }
        }
      }
      var swatchOf = function (title) {
        try {
          var probe = new Option().style;
          probe.color = '';
          probe.color = String(title || '').toLowerCase();
          return probe.color ? probe.color : null;
        } catch (e) { return null; }
      };
      panel.innerHTML = '';
      if (multiAxis) {
        var axesTitle = axes.map(function (a) { return a.name; }).filter(Boolean).join(', ');
        if (axesTitle) {
          var titleEl = document.createElement('div');
          titleEl.className = 'pi-uv-title';
          titleEl.textContent = axesTitle;
          panel.appendChild(titleEl);
        }
      }
      for (var u = 0; u < info.qty; u++) (function (u) {
        var row = document.createElement('div');
        row.className = 'pi-uv-row';
        if (info.qty > 1) { /* the #N badge is noise on a single row */
          var num = document.createElement('span');
          num.className = 'pi-uv-num';
          num.textContent = '#' + (u + 1);
          row.appendChild(num);
        }
        if (multiAxis) {
          row.className += ' pi-uv-row--multi';
          for (var a = 0; a < axes.length; a++) (function (axis) {
            var wrap = document.createElement('span');
            wrap.className = 'pi-uv-axis';
            if (axis.index === imgAxisIndex) {
              var chip = document.createElement('span');
              chip.className = 'pi-uv-swatch pi-uv-swatch--img pi-uv-axis-img';
              chip.hidden = true;
              wrap.appendChild(chip);
            }
            var sel = document.createElement('select');
            sel.className = 'pi-uv-select';
            sel.setAttribute('data-ge-uv-select', String(u));
            sel.setAttribute('data-uv-axis', String(axis.index));
            sel.setAttribute('aria-label', axis.name ? (axis.name + ' #' + (u + 1)) : ('#' + (u + 1)));
            for (var i = 0; i < axis.values.length; i++) {
              var opt = document.createElement('option');
              opt.value = axis.values[i];
              opt.textContent = axis.values[i];
              sel.appendChild(opt);
            }
            wrap.appendChild(sel);
            row.appendChild(wrap);
          })(axes[a]);
          /* Sync every axis control + the image chip from the unit's CURRENT
             variant — also called after a nearest-match resolution snaps a
             sibling axis, so the controls never lie about the selection. */
          var syncRow = function () {
            var cur = null;
            for (var i = 0; i < variants.length; i++) {
              if (String(variants[i].id) === String(unitIds[u])) { cur = variants[i]; break; }
            }
            var sels = row.querySelectorAll('select[data-uv-axis]');
            for (var s = 0; s < sels.length; s++) {
              var k = parseInt(sels[s].getAttribute('data-uv-axis'), 10);
              var val = cur && cur['option' + (k + 1)];
              if (typeof val === 'string') sels[s].value = val;
            }
            var chipEl = row.querySelector('.pi-uv-axis-img');
            if (chipEl) {
              var fim = cur && cur.featured_image;
              var vimg = fim && (typeof fim === 'string' ? fim : (fim.src || fim.url));
              if (typeof vimg === 'string' && /^(https?:)?\/\//.test(vimg)) {
                chipEl.style.backgroundImage = 'url("' + vimg + '")';
                chipEl.hidden = false;
              } else chipEl.hidden = true;
            }
          };
          syncRow();
          row.addEventListener('change', function (ev) {
            var t = ev.target;
            if (!t || !t.getAttribute || t.getAttribute('data-uv-axis') == null) return;
            var k = parseInt(t.getAttribute('data-uv-axis'), 10);
            var next = uvResolveVariant(variants, unitIds[u], k, t.value);
            unitIds[u] = String(next.id);
            syncRow();
            recompute();
          });
        } else if (usePills) {
          var group = document.createElement('div');
          group.className = 'pi-uv-pills';
          group.setAttribute('role', 'radiogroup');
          group.setAttribute('aria-label', '#' + (u + 1));
          group.setAttribute('data-ge-uv-select', String(u));
          /* Swatches mode: pure colour circles (initials circle when the
             variant name isn't a CSS colour) + the selected name after the
             row — the label lives in title/aria instead of the pill body. */
          var selName = null;
          if (swatchOnly) {
            selName = document.createElement('span');
            selName.className = 'pi-uv-sel-name';
            selName.textContent = titleById[String(unitIds[u])] || '';
          }
          for (var i = 0; i < variants.length; i++) (function (v) {
            var pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'pi-uv-pill';
            pill.setAttribute('role', 'radio');
            pill.setAttribute('data-uv-val', String(v.id));
            var label = v.title || String(v.id);
            var sw = swatchOf(v.title);
            if (swatchOnly) {
              pill.classList.add('pi-uv-pill--swatch');
              pill.setAttribute('title', label);
              pill.setAttribute('aria-label', label);
              var dot = document.createElement('span');
              dot.className = 'pi-uv-swatch';
              /* Image-first (the merchant ask): when the variant carries its
                 own image the swatch IS that image; colour dot and initials
                 are the fallbacks. featured_image comes from product|json
                 (object with src) — tolerate string/url shapes too. */
              var fim = v.featured_image;
              var vimg = fim && (typeof fim === 'string' ? fim : (fim.src || fim.url));
              if (typeof vimg === 'string' && /^(https?:)?\/\//.test(vimg)) {
                dot.className += ' pi-uv-swatch--img';
                dot.style.backgroundImage = 'url("' + vimg + '")';
              } else if (sw) dot.style.background = sw;
              else {
                dot.className += ' pi-uv-swatch--initials';
                dot.textContent = label.replace(/[^A-Za-z0-9À-ɏ]+/g, ' ').trim()
                  .split(/\s+/).slice(0, 2)
                  .map(function (w) { return w.charAt(0).toUpperCase(); }).join('');
              }
              pill.appendChild(dot);
            } else {
              if (sw) {
                var dot = document.createElement('span');
                dot.className = 'pi-uv-swatch';
                dot.style.background = sw;
                pill.appendChild(dot);
              }
              pill.appendChild(document.createTextNode(label));
            }
            var sync = function () {
              var on = String(v.id) === String(unitIds[u]);
              pill.classList.toggle('selected', on);
              pill.setAttribute('aria-checked', on ? 'true' : 'false');
            };
            sync();
            pill.addEventListener('click', function () {
              unitIds[u] = String(v.id);
              var kids = group.children;
              for (var k = 0; k < kids.length; k++) {
                var on = kids[k].getAttribute('data-uv-val') === String(unitIds[u]);
                kids[k].classList.toggle('selected', on);
                kids[k].setAttribute('aria-checked', on ? 'true' : 'false');
              }
              if (selName) selName.textContent = titleById[String(unitIds[u])] || '';
              recompute();
            });
            group.appendChild(pill);
          })(variants[i]);
          row.appendChild(group);
          if (selName) row.appendChild(selName);
        } else {
          var sel = document.createElement('select');
          sel.className = 'pi-uv-select';
          sel.setAttribute('data-ge-uv-select', String(u));
          sel.setAttribute('aria-label', '#' + (u + 1));
          for (var i = 0; i < variants.length; i++) {
            var opt = document.createElement('option');
            opt.value = String(variants[i].id);
            opt.textContent = variants[i].title || String(variants[i].id);
            if (String(variants[i].id) === String(unitIds[u])) opt.selected = true;
            sel.appendChild(opt);
          }
          sel.addEventListener('change', function () {
            unitIds[u] = sel.value;
            recompute();
          });
          row.appendChild(sel);
        }
        panel.appendChild(row);
      })(u);
      panel.hidden = false;
      recompute();
    }

    if (Array.isArray(uv.onTier)) uv.onTier.push(function () { render(); });
    render(); /* a default tier may already be selected at load */
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ge-uv="1"]');
    for (var i = 0; i < roots.length; i++) {
      var r = roots[i];
      if (!r.__geUVBooted) { r.__geUVBooted = true; try { initUnitVars(r); } catch (e) {} }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();